import React, { useEffect, useMemo, useState } from 'react';
import { PlayFab } from '../services/PlayFab';
import { HandHistoryCard } from '../components/HandHistoryCard';

type HistoryItem = {
  handId: string;
  timestamp: string;
  position?: string;
  leakTags?: string[];
  strengthTags?: string[];
  customTags?: string[];
  primaryLeakTag?: string | null;
  primaryStrengthTag?: string | null;
  clusterTag?: string | null;
  tagSchemaVersion?: string;
  streetRatings?: Record<string, number>;
  handType?: string;
  statsTaggedAt?: string;
  PK?: string;
  SK?: string;
};

// Extract handId from SK fallback (supports both TS#...#HAND#<id> and SES#...#TS#...#HAND#<id>)
const extractHandIdFromSk = (sk?: string | null): string | null => {
  if (!sk || typeof sk !== 'string') return null;
  const marker = '#HAND#';
  const idx = sk.lastIndexOf(marker);
  if (idx === -1) return null;
  const id = sk.substring(idx + marker.length);
  return id || null;
};

// Dedupe by handId, prefer items with heroProfitBB, then session-scoped SK, then newest timestamp
const dedupeByHandId = (items: any[]): HistoryItem[] => {
  const pickBetter = (a: any, b: any) => {
    const aProf = Number.isFinite(Number(a?.heroProfitBB));
    const bProf = Number.isFinite(Number(b?.heroProfitBB));
    if (aProf !== bProf) return aProf ? a : b;
    const aSes = typeof a?.SK === 'string' && a.SK.startsWith('SES#');
    const bSes = typeof b?.SK === 'string' && b.SK.startsWith('SES#');
    if (aSes !== bSes) return aSes ? a : b;
    // Prefer latest timestamp
    const at = new Date(a?.timestamp || 0).getTime();
    const bt = new Date(b?.timestamp || 0).getTime();
    return at >= bt ? a : b;
  };
  const byId = new Map<string, any>();
  for (const it of items) {
    const id = it?.handId;
    if (!id) continue;
    if (!byId.has(id)) byId.set(id, it);
    else byId.set(id, pickBetter(byId.get(id), it));
  }
  return Array.from(byId.values()) as HistoryItem[];
};

const MobilePanel: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ padding: '12px', background: '#0b1220', borderRadius: 8, marginBottom: 12 }}>
    <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
    <div>{children}</div>
  </div>
);

const Chip: React.FC<{ label: string }> = ({ label }) => (
  <span style={{ display: 'inline-block', padding: '4px 8px', margin: 4, borderRadius: 999, background: '#1b2742', fontSize: 12 }}>{label}</span>
);

// --- Lightweight charts ---
interface BarDatum { label: string; value: number; }

const BarChart: React.FC<{ data: BarDatum[]; maxBars?: number }> = ({ data, maxBars = 5 }) => {
  const bars = data.slice(0, maxBars);
  const max = Math.max(1, ...bars.map(b => b.value));
  return (
    <div>
      {bars.map((b) => (
        <div key={b.label} style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ width: 110, fontSize: 12, opacity: 0.9, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{b.label}</div>
          <div style={{ flex: 1, marginLeft: 8, background: '#13203a', height: 10, borderRadius: 10 }}>
            <div style={{ width: `${(b.value / max) * 100}%`, height: '100%', borderRadius: 10, background: '#2e7dd7' }} />
          </div>
          <div style={{ width: 32, marginLeft: 8, fontSize: 12, opacity: 0.8, textAlign: 'right' }}>{b.value}</div>
        </div>
      ))}
    </div>
  );
};

const DonutChart: React.FC<{ segments: { label: string; value: number; color: string }[]; size?: number; stroke?: number }> = ({ segments, size = 140, stroke = 14 }) => {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = (size - stroke) / 2;
  const c = Math.PI * 2 * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        {segments.map((seg, idx) => {
          const segLen = (seg.value / total) * c;
          const dasharray = `${segLen} ${c - segLen}`;
          const circle = (
            <circle
              key={idx}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="transparent"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeDasharray={dasharray}
              strokeDashoffset={-offset}
            />
          );
          offset += segLen;
          return circle;
        })}
      </g>
      {/* center label */}
      <circle cx={size / 2} cy={size / 2} r={r - stroke * 0.6} fill="#0b1220" />
    </svg>
  );
};

// Session metadata card content
const SessionMeta: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const [meta, setMeta] = useState<{ count: number; latestTs?: string; earliestTs?: string; profitBB?: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch session hands directly and derive count + timestamps from primary items
        const res = await PlayFab.executeCloudScript('getDynamoDBData', { action: 'getSessionHands', sessionId });
        const root = (res as any)?.data?.FunctionResult;
        // Handle shapes: {FunctionResult:{data:[...]}} OR {FunctionResult:{FunctionResult:{data:[...]}}}
        const list = Array.isArray(root?.FunctionResult?.data)
          ? root.FunctionResult.data
          : (Array.isArray(root?.data)
            ? root.data
            : (Array.isArray(root)
              ? root
              : []));
        const count = list.length;
        // Sum profit in BB across hands (robust extraction)
        const sumProfitBB = (() => {
          let total = 0;
          for (const it of list) {
            let v: any = (it as any)?.heroProfitBB;
            if (v == null) {
              try {
                const sum = typeof (it as any)?.summaryJson === 'string' ? JSON.parse((it as any).summaryJson) : (it as any)?.summaryJson;
                v = sum?.heroProfitBB ?? sum?.results?.heroPayoutBB;
              } catch {}
            }
            const n = Number(v);
            if (Number.isFinite(n)) total += n;
          }
          return Math.round(total * 10) / 10;
        })();
        const latestTs = count > 0 ? (list[0]?.timestamp || list[0]?.TS || list[0]?.ts || null) : null; // handler returns sorted desc
        const earliestTs = count > 0 ? (list[count - 1]?.timestamp || list[count - 1]?.TS || list[count - 1]?.ts || null) : null;
        if (!cancelled) setMeta({ count, latestTs: latestTs || undefined, earliestTs: earliestTs || undefined, profitBB: sumProfitBB });
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || 'failed');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);
  if (err) return <div style={{ fontSize: 11, opacity: 0.7 }}>...</div>;
  const when = meta?.latestTs ? new Date(meta.latestTs).toLocaleDateString() : '';
  return (
    <div style={{ fontSize: 11, opacity: 0.85 }}>
      <div>{when}</div>
      <div>{meta?.count ?? 0} hands</div>
      {typeof meta?.profitBB === 'number' && (
        <div style={{ color: (meta.profitBB || 0) >= 0 ? '#2ec97d' : '#e74c3c' }}>{(meta.profitBB || 0) >= 0 ? '+' : ''}{(meta.profitBB || 0).toFixed(1)} BB</div>
      )}
    </div>
  );
};

// Mini hand preview card reused style
const parseCard = (cardStr: string): { rank: string; suit: string } | null => {
  if (!cardStr || cardStr.length !== 2) return null;
  return { rank: cardStr[0], suit: cardStr[1] };
};

const HistoryHandPreview: React.FC<{ hand: any; color: string }> = ({ hand, color }) => {
  const cards = hand.cards || hand.heroCards || null;
  const board = hand.board || [];
  const hero = cards ? [cards.holeCard1, cards.holeCard2].filter(Boolean) : [];
  return (
    <div className="hand-history__item" style={{ borderColor: '#1b2742', borderWidth: 1, borderStyle: 'solid', marginBottom: 8 }}>
      <div className="hand-history__item-content">
        <div className="hand-history__item-row">
          <div className="hand-history__timestamp" style={{ fontSize: 12 }}>ID: {hand.handId}</div>
          <div style={{ fontSize: 12, color, fontWeight: 700 }}>{hand.profitBB > 0 ? '+' : ''}{typeof hand.profitBB === 'number' ? hand.profitBB.toFixed(1) : hand.profitBB} BB</div>
        </div>
        <div className="hand-history__item-row hand-history__cards-row">
          <div className="hand-history__hole-cards">
            <strong>Hero:</strong>
            {hero.length === 2 ? (
              hero.map((c:string) => {
                const pc = parseCard(c);
                return pc ? <HandHistoryCard key={c} rank={pc.rank} suit={pc.suit} /> : null;
              })
            ) : <span>--</span>}
          </div>
          <div className="hand-history__community-cards">
            <strong>Board:</strong>
            {Array.isArray(board) && board.length > 0 ? (
              board.map((c:string) => {
                const pc = parseCard(c);
                return pc ? <HandHistoryCard key={c} rank={pc.rank} suit={pc.suit} /> : null;
              })
            ) : <span>(No board)</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

const PlayerProfilePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [sessions, setSessions] = useState<string[]>([]);
  const [rangeDays, setRangeDays] = useState<number>(90);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [limit] = useState<number>(300);
  const [sessionPage, setSessionPage] = useState<number>(0);

  const computeWindow = (days: number) => {
    const end = new Date();
    const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    return { startIso: start.toISOString(), endIso: end.toISOString() };
  };

  const fetchHistory = async (sessionId: string | null, days: number) => {
    const { startIso, endIso } = computeWindow(days);
    if (sessionId) {
      const histRes = await PlayFab.executeCloudScript('getDynamoDBData', { action: 'getUserHistoryBySession', sessionId: sessionId, startIso, endIso, limit, desc: true });
      const histRoot = (histRes as any)?.data?.FunctionResult;
      const histArray = Array.isArray(histRoot?.FunctionResult?.data)
        ? histRoot.FunctionResult.data
        : (Array.isArray(histRoot?.data)
          ? histRoot.data
          : (Array.isArray(histRoot)
            ? histRoot
            : []));
      const normalizedAll = (histArray as any[]).map((it) => {
        const ensuredId = it?.handId || extractHandIdFromSk(it?.SK) || extractHandIdFromSk(it?.Sk) || extractHandIdFromSk(it?.sk) || '';
        return { ...it, handId: ensuredId } as HistoryItem;
      });
      setHistory(dedupeByHandId(normalizedAll));
    } else {
      const histRes = await PlayFab.executeCloudScript('getDynamoDBData', { action: 'getUserHistory', startIso, endIso, limit, desc: true });
      const histRoot = (histRes as any)?.data?.FunctionResult;
      const histArray = Array.isArray(histRoot?.FunctionResult?.data)
        ? histRoot.FunctionResult.data
        : (Array.isArray(histRoot?.data)
          ? histRoot.data
          : (Array.isArray(histRoot)
            ? histRoot
            : []));
      const normalizedAll = (histArray as any[]).map((it) => {
        const ensuredId = it?.handId || extractHandIdFromSk(it?.SK) || extractHandIdFromSk(it?.Sk) || extractHandIdFromSk(it?.sk) || '';
        return { ...it, handId: ensuredId } as HistoryItem;
      });
      setHistory(dedupeByHandId(normalizedAll));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const sessionsRes = await PlayFab.executeCloudScript('getDynamoDBData', { action: 'getSessions' });
        const sessRoot = (sessionsRes as any)?.data?.FunctionResult;
        const sessionsArray = Array.isArray(sessRoot?.FunctionResult?.data)
          ? sessRoot.FunctionResult.data
          : (Array.isArray(sessRoot?.data)
            ? sessRoot.data
            : (Array.isArray(sessRoot)
              ? sessRoot
              : []));
        if (!cancelled) setSessions(sessionsArray as string[]);
        if (!cancelled) await fetchHistory(null, rangeDays);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load profile data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await fetchHistory(selectedSession, rangeDays);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSession, rangeDays]);

  // Reset session page when sessions list changes
  useEffect(() => { setSessionPage(0); }, [sessions]);

  const aggregates = useMemo(() => {
    const getProfitBB = (it: any): number | null => {
      // Prefer top-level heroProfitBB
      const top = it?.heroProfitBB;
      if (typeof top === 'number' && Number.isFinite(top)) return top;
      if (typeof top === 'string') {
        const n = Number(top);
        if (Number.isFinite(n)) return n;
      }
      // Sometimes profit stored in nested summaryJson
      try {
        const sum = typeof it?.summaryJson === 'string' ? JSON.parse(it.summaryJson) : it?.summaryJson;
        const nested = sum?.heroProfitBB ?? sum?.results?.heroPayoutBB;
        const n = Number(nested);
        return Number.isFinite(n) ? n : null;
      } catch { return null; }
    };
    const leakCount: Record<string, number> = {};
    const strengthCount: Record<string, number> = {};
    const clusterCount: Record<string, number> = {};
    const streetSums: Record<string, { total: number; n: number }> = {};
    for (const it of history) {
      for (const t of it.leakTags || []) leakCount[t] = (leakCount[t] || 0) + 1;
      for (const t of it.strengthTags || []) strengthCount[t] = (strengthCount[t] || 0) + 1;
      const c = it.clusterTag || '';
      if (c) clusterCount[c] = (clusterCount[c] || 0) + 1;
      if (it.streetRatings && typeof it.streetRatings === 'object') {
        for (const [street, val] of Object.entries(it.streetRatings)) {
          const num = Number(val);
          if (!Number.isNaN(num)) {
            if (!streetSums[street]) streetSums[street] = { total: 0, n: 0 };
            streetSums[street].total += num;
            streetSums[street].n += 1;
          }
        }
      }
    }
    const topLeaks = Object.entries(leakCount).sort((a,b)=>b[1]-a[1]).slice(0,10);
    const topStrengths = Object.entries(strengthCount).sort((a,b)=>b[1]-a[1]).slice(0,10);
    const clusters = Object.entries(clusterCount).sort((a,b)=>b[1]-a[1]);
    // Build bar data
    const leakBars: BarDatum[] = topLeaks.map(([label, value]) => ({ label, value }));
    const strengthBars: BarDatum[] = topStrengths.map(([label, value]) => ({ label, value }));
    // Donut segments with stable colors
    const palette = ['#2e7dd7','#2ec97d','#d7b32e','#d7592e','#9b59b6','#e74c3c','#1abc9c'];
    const clusterSegs = clusters.map(([label, value], i) => ({ label, value, color: palette[i % palette.length] }));
    const streetsAvg: BarDatum[] = Object.entries(streetSums).map(([k, v]) => ({ label: k, value: Math.round((v.total / Math.max(1, v.n)) * 10) / 10 }));
    const sortedStreets = streetsAvg.sort((a,b)=> a.label.localeCompare(b.label));
    // Biggest wins/losses by profitBB (robust extraction)
    const profitMapped = history
      .map(h => ({
        handId: h.handId,
        profitBB: getProfitBB(h),
        heroCards: (h as any).heroCards,
        board: (h as any).board
      }))
      .filter(x => typeof x.profitBB === 'number' && Number.isFinite(x.profitBB)) as { handId: string; profitBB: number; heroCards?: any; board?: any }[];
    const sortedByProfit = profitMapped.slice().sort((a,b)=> (b.profitBB - a.profitBB));
    const biggestWins = sortedByProfit.slice(0, 3).map(h => ({ handId: h.handId, profitBB: h.profitBB, cards: h.heroCards, board: h.board }));
    const biggestLosses = sortedByProfit.slice(-3).reverse().map(h => ({ handId: h.handId, profitBB: h.profitBB, cards: h.heroCards, board: h.board }));
    return { topLeaks, topStrengths, clusters, leakBars, strengthBars, clusterSegs, streetsAvg: sortedStreets, biggestWins, biggestLosses };
  }, [history]);

  if (loading) return <div style={{ padding: 12 }}>Loading...</div>;
  if (error) return <div style={{ padding: 12, color: 'salmon' }}>{error}</div>;

  return (
    <div style={{ paddingTop: 12, paddingLeft: 12, paddingRight: 12, paddingBottom: '120px' }}>
      {/* Filters */}
      <MobilePanel title="Filters">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Window</label>
            <select
              value={rangeDays}
              onChange={(e)=> setRangeDays(parseInt(e.target.value))}
              style={{ display: 'block', padding: '6px', background: '#0b1220', color: 'white', borderRadius: 6, border: '1px solid #1b2742' }}
            >
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>6 months</option>
              <option value={365}>1 year</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, opacity: 0.8 }}>Session</label>
            <div>
              {selectedSession ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Chip label={selectedSession} />
                  <button onClick={()=> setSelectedSession(null)} style={{ background: 'transparent', color: '#d7592e', border: 'none' }}>Clear</button>
                </div>
              ) : (
                <div style={{ fontSize: 12, opacity: 0.7 }}>All sessions</div>
              )}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
          The selected window applies to all metrics below.
        </div>
      </MobilePanel>

      <MobilePanel title="Recent Sessions">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {sessions.slice(sessionPage * 4, sessionPage * 4 + 4).map((s) => (
            <div
              key={s}
              onClick={()=> setSelectedSession(String(s))}
              style={{ cursor: 'pointer', background: '#0f1a2e', border: '1px solid #1b2742', borderRadius: 8, padding: 8 }}
            >
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{String(s)}</div>
              <SessionMeta sessionId={String(s)} />
            </div>
          ))}
          {sessions.length === 0 && <div style={{ opacity: 0.7 }}>No sessions yet</div>}
        </div>
        {sessions.length > 4 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
            <button
              onClick={() => setSessionPage(p => Math.max(0, p - 1))}
              disabled={sessionPage === 0}
              style={{ background: 'transparent', color: 'white', opacity: sessionPage === 0 ? 0.4 : 1, border: '1px solid #1b2742', borderRadius: 6, padding: '6px 10px' }}
            >
              ◀ Prev
            </button>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Page {sessionPage + 1} of {Math.max(1, Math.ceil(sessions.length / 4))}
            </div>
            <button
              onClick={() => setSessionPage(p => Math.min(Math.ceil(sessions.length / 4) - 1, p + 1))}
              disabled={sessionPage >= Math.ceil(sessions.length / 4) - 1}
              style={{ background: 'transparent', color: 'white', opacity: sessionPage >= Math.ceil(sessions.length / 4) - 1 ? 0.4 : 1, border: '1px solid #1b2742', borderRadius: 6, padding: '6px 10px' }}
            >
              Next ▶
            </button>
          </div>
        )}
      </MobilePanel>

      {/* Average Street Ratings next after sessions */}
      <MobilePanel title="Average Street Ratings">
        {aggregates.streetsAvg && aggregates.streetsAvg.length > 0 ? (
          <BarChart data={aggregates.streetsAvg} maxBars={10} />
        ) : (
          <div style={{ opacity: 0.7 }}>No ratings yet</div>
        )}
      </MobilePanel>

      <MobilePanel title="Improvement Areas">
        {aggregates.clusterSegs.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <DonutChart segments={aggregates.clusterSegs} />
            <div style={{ marginLeft: 12 }}>
              {aggregates.clusterSegs.map((s) => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ width: 10, height: 10, background: s.color, display: 'inline-block', borderRadius: 2, marginRight: 8 }} />
                  <span style={{ fontSize: 12 }}>{s.label}</span>
                  <span style={{ fontSize: 12, opacity: 0.7, marginLeft: 6 }}>x{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ opacity: 0.7 }}>No clusters yet</div>
        )}
      </MobilePanel>

      <MobilePanel title="Leaks">
        {aggregates.leakBars.length > 0 ? (
          <BarChart data={aggregates.leakBars} maxBars={5} />
        ) : (
          <div style={{ opacity: 0.7 }}>No tags yet</div>
        )}
      </MobilePanel>

      <MobilePanel title="Strengths">
        {aggregates.strengthBars.length > 0 ? (
          <BarChart data={aggregates.strengthBars} maxBars={5} />
        ) : (
          <div style={{ opacity: 0.7 }}>No tags yet</div>
        )}
      </MobilePanel>

      <MobilePanel title="Biggest Wins (BB)">
        {aggregates.biggestWins && aggregates.biggestWins.length > 0 ? (
          <div>
            {aggregates.biggestWins.map((h:any) => (
              <HistoryHandPreview key={h.handId} hand={h} color="#2ec97d" />
            ))}
          </div>
        ) : (
          <div style={{ opacity: 0.7 }}>No profit data yet</div>
        )}
      </MobilePanel>

      <MobilePanel title="Biggest Losses (BB)">
        {aggregates.biggestLosses && aggregates.biggestLosses.length > 0 ? (
          <div>
            {aggregates.biggestLosses.map((h:any) => (
              <HistoryHandPreview key={h.handId} hand={h} color="#e74c3c" />
            ))}
          </div>
        ) : (
          <div style={{ opacity: 0.7 }}>No profit data yet</div>
        )}
      </MobilePanel>
      {/* Bottom fixed CTA */}
      <div style={{ position: 'fixed', left: 12, right: 12, bottom: 'calc(12px + env(safe-area-inset-bottom))', zIndex: 1000 }}>
        <button
          onClick={() => { /* no-op for now */ }}
          style={{
            width: '100%',
            padding: '12px 14px',
            background: '#2e7dd7',
            border: 'none',
            color: 'white',
            borderRadius: 8,
            fontWeight: 700,
            boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
          }}
        >
          Generate Training Exercises
        </button>
      </div>
    </div>
  );
};

export default PlayerProfilePage;


